import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, BadgePoundSterling } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { Input } from "@/components/ui/input";

const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Admin: record commission payouts for one partner and see what is still owed.
export const PayoutLog = ({ partnerId }) => {
  const [stats, setStats] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/dropshippers/${partnerId}/payouts`).then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, [partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const value = parseFloat(amount);
    if (!(value > 0)) { toast.error("Enter the amount paid out"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/dropshippers/${partnerId}/payouts`, { amount: value, note });
      setStats(data); setAmount(""); setNote("");
      toast.success(`Payout of ${gbp(value)} recorded`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async (pid) => {
    if (!window.confirm("Remove this payout entry?")) return;
    try {
      const { data } = await api.delete(`/admin/dropshippers/${partnerId}/payouts/${pid}`);
      setStats(data); toast.success("Payout removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!stats) return null;
  return (
    <div className="mt-4 border border-border bg-background p-4" data-testid={`payout-log-${partnerId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-heading text-sm font-bold">Commission payouts</div>
        <div className="mono flex flex-wrap gap-2 text-[11px]">
          <span className="border border-border px-2 py-1">Earned (paid orders): <b>{gbp(stats.commission_earned)}</b></span>
          <span className="border border-border px-2 py-1">Paid out: <b data-testid={`payout-paid-out-${partnerId}`}>{gbp(stats.paid_out)}</b></span>
          <span className={`border px-2 py-1 ${stats.owed > 0 ? "border-primary text-primary" : "border-border"}`}>Still owed: <b data-testid={`payout-owed-${partnerId}`}>{gbp(stats.owed)}</b></span>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Amount paid £</label>
          <Input data-testid={`payout-amount-${partnerId}`} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-32 rounded-none mono" placeholder={stats.owed > 0 ? stats.owed.toFixed(2) : "0.00"} />
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Note (optional)</label>
          <Input data-testid={`payout-note-${partnerId}`} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 rounded-none" placeholder="e.g. BTC sent 14 Jun, txid …" />
        </div>
        <button data-testid={`payout-add-${partnerId}`} onClick={add} disabled={busy} className="inline-flex items-center gap-1.5 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BadgePoundSterling className="h-3.5 w-3.5" />} Record payout
        </button>
      </div>
      {stats.payouts.length > 0 && (
        <ul className="mt-3 divide-y divide-border text-xs" data-testid={`payout-list-${partnerId}`}>
          {stats.payouts.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2" data-testid={`payout-row-${p.id}`}>
              <span className="mono text-muted-foreground">{when(p.created_at)}</span>
              <span className="flex-1 truncate">{p.note || "—"}</span>
              <span className="mono font-semibold">{gbp(p.amount)}</span>
              <button onClick={() => remove(p.id)} data-testid={`payout-delete-${p.id}`} className="text-muted-foreground hover:text-destructive" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
